import React from 'react';
import { Element, Form } from './FormShareEmail.styles';
import { Text } from '@/components/typography';
import TextField from '@/components/form/TextField/TextField';
import Fieldset from '@/components/form/Fieldset/Fieldset';
import Label from '@/components/form/Label/Label';
import { Button } from '@/components/button';
import FieldGroup from '@/components/form/FieldGroup/FieldGroup';
import TextArea from '@/components/form/Textarea/TextArea';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Spinner } from '@/components/loading';
import { Banner } from '@/components/notification';
import useFeedbackHandler from '@/features/forms/hooks/useFeedbackHandler';
import formPayloadParser, { FormPayloadDataType } from '@/features/forms/formPayloadParser';
import FeedbackHandler from '@/features/forms/components/FeedbackHandler/FeedbackHandler';
import validationScheme, { FormEmailShareDataType } from '@/features/forms/FormShareMail/validationScheme';
import { FORMS_ENDPOINT } from '@/helpers/browser';
import SubmittedButton from '@/features/forms/components/SubmittedButton/SubmittedButton';
import { trackGTMEvent } from '@/utils/tracking';
import { useTranslations } from 'next-intl';

export interface FormShareEmailType {
  productUuid?: string;
  productName?: 'pacote' | 'oferta' | 'cruzeiro';
}
const FormShareEmail = ({ productUuid, productName }: FormShareEmailType) => {
  const t = useTranslations('FormShareEmail');

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid, errors },
  } = useForm<FormEmailShareDataType>({
    resolver: zodResolver(validationScheme),
  });

  const { feedback, submitting, submitted, setSubmitted, submitRequestHandler, resetFeedback } = useFeedbackHandler();

  const onSubmit = async (data: FormEmailShareDataType) => {
    const payload = formPayloadParser({ productUuid, productName, ...data } as FormPayloadDataType, 'emailshare');

    if (isValid) {
      submitRequestHandler(FORMS_ENDPOINT.form_email_share, payload).then((done) => {
        if (done) {
          // console.log(`DONE`, done);
          reset();
          trackGTMEvent({ eventCategory: 'Form', eventAction: 'submit', eventLabel: 'form-share-email' });
        }
      });
    }
  };

  return (
    <Element>
      {(submitting && (
        <Spinner>
          <Text>{t('Enviando sua mensagem, por favor, aguarde')}...</Text>
        </Spinner>
      )) || (
        <>
          <FeedbackHandler {...feedback} />
          <Banner
            show={Object.keys(errors).length > 0}
            appearance="error"
            message={`${t('Por favor, corrigir o(s) erro(s) abaixo')}:`}
          />
          {!submitted && (
            <>
              <Text size="base" color="neutral_600">
                {t(
                  'Compartilhe com um amigo, preencha corretamente os campos abaixo para enviar este pacote por e-mail'
                )}
                .
              </Text>
              <Form
                action="/proxy/action/form-shareemail"
                name="formShareEmail"
                method="post"
                onSubmit={handleSubmit(onSubmit)}
                noValidate
              >
                <Fieldset>
                  <legend>{t('Seus dados')}</legend>
                  <FieldGroup>
                    <Label htmlFor="senderName" required>
                      {t('Seu Nome')}
                    </Label>
                    <TextField
                      id="senderName"
                      {...register('senderName')}
                      error={errors.senderName?.message}
                      autoCapitalize="off"
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label htmlFor="senderEmail" required>
                      {t('Seu Email')}
                    </Label>
                    <TextField
                      id="senderEmail"
                      type="email"
                      {...register('senderEmail')}
                      error={errors.senderEmail?.message}
                      autoCapitalize="off"
                    />
                  </FieldGroup>
                </Fieldset>

                <Fieldset>
                  <legend>{t('Dados do destinatário')}</legend>
                  <FieldGroup>
                    <Label htmlFor="receiverName" required>
                      {t('Nome Destinatário')}
                    </Label>
                    <TextField
                      id="receiverName"
                      {...register('receiverName')}
                      error={errors.receiverName?.message}
                      autoCapitalize="off"
                    />
                  </FieldGroup>
                  <FieldGroup>
                    <Label htmlFor="receiverEmail" required>
                      {t('Email Destinatário')}
                    </Label>
                    <TextField
                      id="receiverEmail"
                      {...register('receiverEmail')}
                      error={errors.receiverEmail?.message}
                      autoCapitalize="off"
                    />
                  </FieldGroup>
                </Fieldset>

                <Fieldset>
                  <legend>{t('Mensagem')}</legend>
                  <FieldGroup>
                    <TextArea
                      {...register('message')}
                      placeholder={t('Descreva alguma mensagem para o destinatário')}
                    />
                  </FieldGroup>
                </Fieldset>

                <Button type="submit" block appearance="primary" disabled={submitting}>
                  {t('ENVIAR')}
                </Button>
              </Form>
            </>
          )}

          {submitted && <SubmittedButton setSubmitted={setSubmitted} resetFeedback={resetFeedback} />}
        </>
      )}
    </Element>
  );
};

export default FormShareEmail;
